import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type DemoVerificationStatus = 'not_verified' | 'pending' | 'verified' | 'expired';

interface DemoModeContextType {
  isDemoMode: boolean;
  enableDemoMode: () => void;
  disableDemoMode: () => void;
  
  // QR Verification Demo State
  demoVerificationStatus: DemoVerificationStatus;
  demoClinicName: string | null;
  demoVerifiedAt: string | null;
  setDemoVerificationStatus: (status: DemoVerificationStatus) => void;
  simulateQRScan: (token: string) => Promise<{ success: boolean; clinicName?: string; error?: string }>;
  resetDemoVerification: () => void;
  
  // Demo Tokens
  validDemoTokens: string[];
  expiredDemoTokens: string[];
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

// Valid demo tokens with clinic associations
const VALID_TOKENS = [
  'DEMO-1234', // Sapphire Medical Clinic
  'TEST-5678', // Curaleaf Clinic
  'CBTS-9012', // Cannabis Clinic Cardiff
  'MCCS-3456', // Medical Cannabis Clinics
];

const EXPIRED_TOKENS = [
  'EXPR-0000', // Expired token
  'OLD1-2345', // Old expired token
];

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState(false);
  
  // QR Verification Demo State
  const [demoVerificationStatus, setDemoVerificationStatus] = useState<DemoVerificationStatus>('not_verified');
  const [demoClinicName, setDemoClinicName] = useState<string | null>(null);
  const [demoVerifiedAt, setDemoVerifiedAt] = useState<string | null>(null);

  const enableDemoMode = () => {
    console.log('🔧 DEMO MODE ENABLED - Bypassing authentication');
    setIsDemoMode(true);
    sessionStorage.setItem('cannabias_demo_mode', 'true');
  };

  const disableDemoMode = () => {
    console.log('🔧 DEMO MODE DISABLED');
    setIsDemoMode(false);
    sessionStorage.removeItem('cannabias_demo_mode');
    resetDemoVerification();
  };

  const resetDemoVerification = () => {
    setDemoVerificationStatus('not_verified');
    setDemoClinicName(null);
    setDemoVerifiedAt(null);
    sessionStorage.removeItem('demo_verification_status');
    sessionStorage.removeItem('demo_clinic_name');
    sessionStorage.removeItem('demo_verified_at');
  };

  const simulateQRScan = async (token: string): Promise<{ success: boolean; clinicName?: string; error?: string }> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const upperToken = token.toUpperCase().trim();

    // Check if expired
    if (EXPIRED_TOKENS.includes(upperToken)) {
      return {
        success: false,
        error: 'This token has expired. Please request a new QR code from your clinic.'
      };
    }

    // Check if valid
    if (!VALID_TOKENS.includes(upperToken)) {
      return {
        success: false,
        error: 'Invalid token. Please check the code and try again.'
      };
    }

    // Map tokens to clinic names
    const clinicMap: Record<string, string> = {
      'DEMO-1234': 'Sapphire Medical Clinic',
      'TEST-5678': 'Curaleaf Clinic',
      'CBTS-9012': 'Cannabis Clinic Cardiff',
      'MCCS-3456': 'Medical Cannabis Clinics',
    };

    const clinicName = clinicMap[upperToken] || 'Demo Clinic';
    const verifiedAt = new Date().toISOString();

    // Update state
    setDemoVerificationStatus('verified');
    setDemoClinicName(clinicName);
    setDemoVerifiedAt(verifiedAt);

    // Persist to sessionStorage
    sessionStorage.setItem('demo_verification_status', 'verified');
    sessionStorage.setItem('demo_clinic_name', clinicName);
    sessionStorage.setItem('demo_verified_at', verifiedAt);

    console.log('✅ DEMO: QR verification successful', { token: upperToken, clinicName });

    return {
      success: true,
      clinicName
    };
  };

  // Restore demo verification state from sessionStorage
  useEffect(() => {
    const storedDemoMode = sessionStorage.getItem('cannabias_demo_mode');
    if (storedDemoMode === 'true') {
      console.log('🔧 DEMO MODE RESTORED from session storage');
      setIsDemoMode(true);
    }

    const storedStatus = sessionStorage.getItem('demo_verification_status') as DemoVerificationStatus;
    const storedClinic = sessionStorage.getItem('demo_clinic_name');
    const storedVerifiedAt = sessionStorage.getItem('demo_verified_at');

    if (storedStatus) {
      setDemoVerificationStatus(storedStatus);
      setDemoClinicName(storedClinic);
      setDemoVerifiedAt(storedVerifiedAt);
      console.log('🔧 DEMO VERIFICATION RESTORED', { status: storedStatus, clinic: storedClinic });
    }
  }, []);

  return (
    <DemoModeContext.Provider value={{ 
      isDemoMode, 
      enableDemoMode, 
      disableDemoMode,
      demoVerificationStatus,
      demoClinicName,
      demoVerifiedAt,
      setDemoVerificationStatus,
      simulateQRScan,
      resetDemoVerification,
      validDemoTokens: VALID_TOKENS,
      expiredDemoTokens: EXPIRED_TOKENS,
    }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}
